"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  Plus,
  Minus,
  Trash2,
  X,
  ChevronRight,
  ShoppingBag,
} from "lucide-react";

import {
  add_cart_product,
  quantityDecrement,
  remove_product,
} from "@/redux/features/cart";

import {
  useAppDispatch,
  useAppSelector,
} from "@/redux/hook";

const FloatingBasket = () => {
  const dispatch = useAppDispatch();
  const pathname = usePathname();

  // =====================================================
  // CHECKOUT PAGE HIDE LOGIC
  // =====================================================
  if (pathname && pathname.startsWith("/checkout")) {
    return null;
  }

    if (pathname && pathname.startsWith("/order-invoice")) {
    return null;
  }


  // =====================================================
  // CART STATE
  // =====================================================
  const cart_products = useAppSelector(
    (state: any) => state.cart?.cart_products || []
  );

  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // =====================================================
  // AUDIO SYNTHESIZER (NO EXTERNAL MP3 NEEDED)
  // =====================================================
  const playPopSound = (type: "add" | "pop" | "delete" = "pop") => {
    try {
      const AudioCtx =
        window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;

      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.connect(gain);
      gain.connect(ctx.destination);

      const now = ctx.currentTime;

      if (type === "add") {
        // High-pitched cheerful double pop
        osc.type = "sine";
        osc.frequency.setValueAtTime(400, now);
        osc.frequency.exponentialRampToValueAtTime(800, now + 0.08);

        gain.gain.setValueAtTime(0.3, now);
        gain.gain.exponentialRampToValueAtTime(0.01, now + 0.12);

        osc.start(now);
        osc.stop(now + 0.12);
      } else if (type === "delete") {
        // Low soft pop for removal
        osc.type = "triangle";
        osc.frequency.setValueAtTime(300, now);
        osc.frequency.exponentialRampToValueAtTime(120, now + 0.1);

        gain.gain.setValueAtTime(0.25, now);
        gain.gain.exponentialRampToValueAtTime(0.01, now + 0.1);

        osc.start(now);
        osc.stop(now + 0.1);
      } else {
        // Standard soft click/pop
        osc.type = "sine";
        osc.frequency.setValueAtTime(500, now);
        osc.frequency.exponentialRampToValueAtTime(650, now + 0.05);

        gain.gain.setValueAtTime(0.2, now);
        gain.gain.exponentialRampToValueAtTime(0.01, now + 0.06);

        osc.start(now);
        osc.stop(now + 0.06);
      }
    } catch (e) {
      // Ignore audio restriction errors on initial user interaction
    }
  };

  // =====================================================
  // TOTALS
  // =====================================================
  const totalItems = cart_products.reduce(
    (total: number, item: any) => total + Number(item?.orderQuantity || 0),
    0
  );

  const totalPrice = cart_products.reduce((total: number, item: any) => {
    const quantity = Number(item?.orderQuantity || 0);
    const price = Number(
      item?.discountedRetailPrice ??
      item?.retailPrice ??
      item?.price ??
      0
    );
    return total + price * quantity;
  }, 0);

  const formatPrice = (price: number) => {
    return price.toLocaleString("en-BD", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // =====================================================
  // HANDLERS WITH SOUND EFFECTS
  // =====================================================
  const handleIncrement = (item: any) => {
    playPopSound("add");
    dispatch(
      add_cart_product({
        ...item,
        orderQuantity: 1,
      })
    );
  };

  const handleDecrement = (item: any) => {
    const quantity = Number(item?.orderQuantity || 0);
    if (quantity <= 1) return;

    playPopSound("pop");
    dispatch(
      quantityDecrement({
        id: item?.id,
      })
    );
  };

  const handleRemove = (item: any) => {
    playPopSound("delete");
    dispatch(
      remove_product({
        id: item?.id,
        title: item?.name || item?.title || "Product",
      })
    );
  };

  const handleToggleBag = () => {
    playPopSound("pop");
    setIsOpen(!isOpen);
  };

  // Up to 3 product preview images inside the floating bag
  const bagItemsPreview = cart_products.slice(0, 3);

  return (
    <>
      {/* =================================================
          FLOATING SHOPPING BAG BUTTON
      ================================================= */}
      <button
        type="button"
        onClick={handleToggleBag}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        aria-label="Toggle shopping bag"
        style={{
          position: "fixed",
          right: "0",
          top: "50%",
          transform: isOpen
            ? "translateY(-50%) scale(0.9)"
            : isHovered
            ? "translateY(-50%) translateX(-4px)"
            : "translateY(-50%)",
          opacity: isOpen ? 0 : 1,
          pointerEvents: isOpen ? "none" : "auto",
          zIndex: 9999,
          background: "transparent",
          border: "none",
          cursor: "pointer",
          padding: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          transition: "all 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      >
        {/* BAG HANDLE / STRAP */}
        <div
          style={{
            width: "32px",
            height: "16px",
            border: "3px solid #ff851b",
            borderBottom: "none",
            borderTopLeftRadius: "16px",
            borderTopRightRadius: "16px",
            marginBottom: "-2px",
            boxSizing: "border-box",
          }}
        />

        {/* MAIN BAG BODY */}
        <div
          style={{
            position: "relative",
            width: "78px",
            background: "#ff851b",
            borderTopLeftRadius: "14px",
            borderBottomLeftRadius: "18px",
            boxShadow: "0 10px 20px rgba(0, 0, 0, 0.15)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "12px 6px 8px",
            overflow: "hidden",
          }}
        >
          {/* ITEM COUNT BADGE */}
          {totalItems > 0 && (
            <div
              style={{
                position: "absolute",
                top: "4px",
                right: "4px",
                background: "#000000",
                color: "#ffffff",
                fontSize: "11px",
                fontWeight: "900",
                height: "20px",
                minWidth: "20px",
                borderRadius: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "0 5px",
                border: "2px solid #ffffff",
                zIndex: 4,
              }}
            >
              {totalItems}
            </div>
          )}

          {/* ADDED PRODUCTS IMAGE PREVIEW INSIDE BAG */}
          <div
            style={{
              width: "100%",
              height: "36px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              margin: "2px 0 6px",
            }}
          >
            {bagItemsPreview.length > 0 ? (
              <div style={{ display: "flex", alignItems: "center" }}>
                {bagItemsPreview.map((item: any, idx: number) => {
                  const img = item?.image || item?.thumbnail || "/noimage.png";
                  return (
                    <div
                      key={idx}
                      style={{
                        width: "28px",
                        height: "28px",
                        borderRadius: "50%",
                        border: "2px solid #ffffff",
                        overflow: "hidden",
                        position: "relative",
                        marginLeft: idx > 0 ? "-10px" : "0",
                        zIndex: 3 - idx,
                        background: "#ffffff",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
                      }}
                    >
                      <Image
                        src={img}
                        alt="product image"
                        fill
                        sizes="28px"
                        style={{ objectFit: "cover" }}
                      />
                    </div>
                  );
                })}
              </div>
            ) : (
              <ShoppingBag size={22} color="#ffffff" strokeWidth={2.2} />
            )}
          </div>

          {/* SOLID WHITE CONTAINER FOR BOLD BLACK PRICE */}
          <div
            style={{
              width: "100%",
              background: "#ffffff",
              borderRadius: "8px",
              padding: "4px 2px",
              textAlign: "center",
            }}
          >
            <span
              style={{
                fontSize: "12px",
                fontWeight: "900",
                color: "#000000",
                letterSpacing: "-0.3px",
                display: "block",
                lineHeight: "1.1",
              }}
            >
              ৳{formatPrice(totalPrice)}
            </span>
          </div>
        </div>
      </button>

      {/* =================================================
          EXPANDED BASKET DRAWER (OPENING ANIMATION)
      ================================================= */}
      <div
        style={{
          position: "fixed",
          right: "16px",
          bottom: "16px",
          width: "380px",
          maxWidth: "calc(100vw - 32px)",
          maxHeight: "min(640px, calc(100vh - 32px))",
          background: "#ffffff",
          borderRadius: "20px",
          overflow: "hidden",
          zIndex: 10000,
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
          border: "1px solid #eef0f2",
          transformOrigin: "bottom right",
          transform: isOpen ? "scale(1)" : "scale(0)",
          opacity: isOpen ? 1 : 0,
          pointerEvents: isOpen ? "auto" : "none",
          transition: "transform 0.35s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.25s ease",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #f0f2f5",
            background: "#ffffff",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div
              style={{
                width: "38px",
                height: "38px",
                borderRadius: "12px",
                background: "#ff851b",
                color: "#ffffff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ShoppingBag size={20} strokeWidth={2.2} />
            </div>
            <div>
              <h3
                style={{
                  margin: 0,
                  fontSize: "16px",
                  fontWeight: "700",
                  color: "#111827",
                }}
              >
                Your Shopping Bag
              </h3>
              <span style={{ fontSize: "12px", color: "#6b7280" }}>
                {totalItems} {totalItems === 1 ? "item" : "items"} inside bag
              </span>
            </div>
          </div>

          <button
            type="button"
            onClick={() => {
              playPopSound("pop");
              setIsOpen(false);
            }}
            aria-label="Close bag"
            style={{
              width: "32px",
              height: "32px",
              border: "none",
              borderRadius: "50%",
              background: "#f3f4f6",
              color: "#4b5563",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <X size={16} strokeWidth={2.5} />
          </button>
        </div>

        {/* Items List */}
        <div
          style={{
            flex: "1 1 auto",
            overflowY: "auto",
            padding: "8px 16px",
          }}
        >
          {cart_products.length === 0 ? (
            <div
              style={{
                padding: "40px 20px",
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  width: "64px",
                  height: "64px",
                  borderRadius: "50%",
                  background: "#fff3e6",
                  color: "#ff851b",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "12px",
                }}
              >
                <ShoppingBag size={32} strokeWidth={1.5} />
              </div>
              <h4
                style={{
                  margin: "0 0 4px",
                  fontSize: "15px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                Your bag is empty
              </h4>
              <p style={{ margin: 0, fontSize: "13px", color: "#9ca3af" }}>
                Add items to see them inside your bag
              </p>
            </div>
          ) : (
            cart_products.map((item: any) => {
              const quantity = Number(item?.orderQuantity || 0);
              const unitPrice = Number(
                item?.discountedRetailPrice ??
                item?.retailPrice ??
                item?.price ??
                0
              );
              const lineTotal = unitPrice * quantity;
              const productName = item?.name || item?.title || "Product";
              const productImage =
                item?.image || item?.thumbnail || "/noimage.png";

              return (
                <div
                  key={item?.id || item?._id || productName}
                  style={{
                    display: "flex",
                    gap: "12px",
                    padding: "12px 0",
                    borderBottom: "1px solid #f3f4f6",
                  }}
                >
                  <div
                    style={{
                      position: "relative",
                      width: "60px",
                      height: "60px",
                      borderRadius: "10px",
                      overflow: "hidden",
                      background: "#f9fafb",
                      border: "1px solid #f3f4f6",
                      flexShrink: 0,
                    }}
                  >
                    <Image
                      src={productImage}
                      alt={productName}
                      fill
                      sizes="60px"
                      style={{ objectFit: "contain", padding: "4px" }}
                    />
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: "8px",
                      }}
                    >
                      <h4
                        style={{
                          margin: 0,
                          fontSize: "13px",
                          fontWeight: "600",
                          color: "#1f2937",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {productName}
                      </h4>
                      <button
                        type="button"
                        onClick={() => handleRemove(item)}
                        aria-label={`Remove ${productName}`}
                        style={{
                          border: "none",
                          background: "transparent",
                          color: "#ef4444",
                          cursor: "pointer",
                          padding: "0",
                        }}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>

                    {item?.variant && (
                      <span style={{ fontSize: "11px", color: "#6b7280" }}>
                        {item.variant}
                      </span>
                    )}

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginTop: "8px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          border: "1px solid #e5e7eb",
                          borderRadius: "20px",
                          padding: "2px 6px",
                          gap: "8px",
                          background: "#ffffff",
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => handleDecrement(item)}
                          disabled={quantity <= 1}
                          style={{
                            border: "none",
                            background: "transparent",
                            cursor: quantity <= 1 ? "not-allowed" : "pointer",
                            opacity: quantity <= 1 ? 0.4 : 1,
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <Minus size={12} />
                        </button>
                        <span
                          style={{
                            fontSize: "12px",
                            fontWeight: "700",
                            color: "#111827",
                          }}
                        >
                          {quantity}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleIncrement(item)}
                          style={{
                            border: "none",
                            background: "transparent",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <Plus size={12} />
                        </button>
                      </div>

                      <span
                        style={{
                          fontSize: "13px",
                          fontWeight: "700",
                          color: "#111827",
                        }}
                      >
                        ৳{formatPrice(lineTotal)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer Checkout */}
        <div
          style={{
            padding: "16px",
            borderTop: "1px solid #f0f2f5",
            background: "#ffffff",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "12px",
            }}
          >
            <span style={{ fontSize: "14px", color: "#4b5563" }}>Subtotal</span>
            <strong style={{ fontSize: "18px", color: "#111827" }}>
              ৳{formatPrice(totalPrice)}
            </strong>
          </div>

          {cart_products.length > 0 ? (
            <Link
              href="/checkout"
              onClick={() => setIsOpen(false)}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                width: "100%",
                height: "48px",
                background: "#ff851b",
                color: "#ffffff",
                borderRadius: "14px",
                textDecoration: "none",
                fontWeight: "700",
                fontSize: "15px",
              }}
            >
              Proceed to Checkout
              <ChevronRight size={18} />
            </Link>
          ) : (
            <button
              type="button"
              disabled
              style={{
                width: "100%",
                height: "48px",
                background: "#e5e7eb",
                color: "#9ca3af",
                border: "none",
                borderRadius: "14px",
                fontWeight: "700",
                fontSize: "15px",
                cursor: "not-allowed",
              }}
            >
              Proceed to Checkout
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default FloatingBasket;